"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoggerCore = void 0;
const tslib_1 = require("tslib");
/* eslint-disable dot-notation,no-continue */
const log_node_1 = require("@video/log-node");
const debug_1 = tslib_1.__importDefault(require("debug"));
const events_1 = require("events");
const logger_http_writer_1 = require("./logger-http-writer");
const colors_1 = require("./utils/colors");
const nop = (msg) => {
    // do nothing
};
const instanceCounter = new WeakMap();
function flatData(result, ...args) {
    for (const data of args) {
        if (typeof data !== "object" || Array.isArray(data)) {
            // skip invalid data
            console.error("Invalid logger data:", data);
            continue;
        }
        if ((0, log_node_1.isSerializableObject)(data)) {
            // unpack objects with toJSON() methods
            flatData(result, data.toJSON());
            continue;
        }
        const { aggregates } = data, context = tslib_1.__rest(data, ["aggregates"]);
        if (typeof aggregates === "object") {
            for (const [key, val] of Object.entries(aggregates)) {
                result.aggregates[key] = val;
            }
        }
        for (const [key, val] of Object.entries(context)) {
            result[key] = val == null ? val : JSON.parse(JSON.stringify(val));
        }
    }
}
class LoggerCore {
    constructor(name) {
        this.debug = nop;
        this.deprecated = nop;
        this.error = nop;
        this.fatal = nop;
        this.info = nop;
        this.local = nop;
        this.network = nop;
        this.notice = nop;
        this.timing = nop;
        this.trace = nop;
        this.warn = nop;
        this.packageContext = {
            meta: {},
        };
        this.messageContext = {};
        this.attachedObjects = new Set();
        this.emitter = new events_1.EventEmitter();
        this.printer = {};
        this.lastLogged = {};
        this.name = name;
        this.packageContext.meta = { package: this.name };
        this.httpWriter = new logger_http_writer_1.LoggerGlobal();
        this.httpWriter.registerPackage(this);
        this.proxyingToEmitter = false;
        this.debugLogger = (0, debug_1.default)(this.name);
        log_node_1.LEVELS.forEach((l) => {
            this[l] = this.log.bind(this, l);
            this.printer[l] = this.printLog.bind(this, l);
        });
    }
    flush() {
        this.httpWriter.send();
    }
    attachObject(obj) {
        obj.on("disposed", () => {
            this.attachedObjects.delete(obj);
        });
        this.attachedObjects.add(obj);
        return this;
    }
    setMessageAggregate(key, value) {
        if (value === undefined) {
            return this.removeMessageAggregate(key);
        }
        if (this.messageContext.aggregates == null) {
            this.messageContext.aggregates = { [key]: value };
        }
        else {
            this.messageContext.aggregates[key] = value;
        }
        return this;
    }
    appendChain(klass) {
        var _a, _b;
        let index = 0;
        if (instanceCounter.has(klass)) {
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            index = instanceCounter.get(klass) + 1;
        }
        const current = (_b = (_a = this.messageContext.aggregates) === null || _a === void 0 ? void 0 : _a["chain"]) !== null && _b !== void 0 ? _b : "";
        const chain = current === "" ? `${klass.displayName}#${index}` : `${current}:${klass.displayName}#${index}`;
        this.setMessageAggregate("chain", chain);
        instanceCounter.set(klass, index);
        return this;
    }
    removeMessageAggregate(key) {
        if (this.messageContext.aggregates != null) {
            if (this.messageContext.aggregates[key] != null) {
                delete this.messageContext.aggregates[key];
                if (Object.keys(this.messageContext.aggregates).length === 0) {
                    delete this.messageContext.aggregates;
                }
            }
        }
        return this;
    }
    clearMessageAggregates() {
        this.messageContext = {};
        return this;
    }
    setLoggerAggregate(key, value) {
        if (this.packageContext.aggregates == null) {
            this.packageContext.aggregates = { [key]: value };
        }
        else {
            this.packageContext.aggregates[key] = value;
        }
        return this;
    }
    setLoggerMeta(key, value) {
        if (this.packageContext.meta == null) {
            this.packageContext.meta = { [key]: value, package: this.name };
        }
        else {
            this.packageContext.meta[key] = value;
        }
        return this;
    }
    getLoggerMeta(key) {
        var _a;
        if (((_a = this.packageContext) === null || _a === void 0 ? void 0 : _a.meta) && this.packageContext.meta[key] != null) {
            return this.packageContext.meta[key];
        }
        return undefined;
    }
    log(level, message, data) {
        var _a, _b;
        (_b = (_a = this.printer) === null || _a === void 0 ? void 0 : _a[level]) === null || _b === void 0 ? void 0 : _b.call(_a, message, data, this.messageContext.aggregates);
        const [msg, logData] = this.handleLogData(message, data, true);
        const log = Object.assign({ message: `${msg}`, level: level === "trace" ? "debug" : level, time: new Date().toISOString() }, logData);
        this.httpWriter.add(log, this);
        if (this.proxyingToEmitter) {
            this.emitter.emit(level, message, logData);
        }
    }
    throttledLog(level, ms, message, data) {
        var _a;
        if (this.lastLogged[message] == null || Date.now() - ((_a = this.lastLogged[message]) !== null && _a !== void 0 ? _a : 0) > ms) {
            this.lastLogged[message] = Date.now();
            this.log(level, message, data);
        }
    }
    handleLogData(message, data, logError = false) {
        var _a, _b, _c;
        let msg = "";
        data !== null && data !== void 0 ? data : (data = {});
        if (message instanceof Error) {
            msg = message.message;
            if ((0, log_node_1.isSerializableObject)(message)) {
                data["err"] = message;
            }
            else {
                data["err"] = {
                    message: message.message,
                    name: message.name,
                    stack: message.stack,
                };
            }
        }
        else {
            msg = message;
        }
        let result = { aggregates: {} };
        try {
            flatData(result, { aggregates: this.packageContext.aggregates }, { aggregates: this.messageContext.aggregates }, data, ...this.attachedObjects);
        }
        catch (err) {
            const errMsg = `logging data were dropped because it's not serializable; log message: ${message}; error: ${err};`;
            if (logError) {
                this.printLog("error", errMsg);
            }
            result = {
                aggregates: {
                    chain: (_a = result.aggregates) === null || _a === void 0 ? void 0 : _a.chain,
                    contextId: (_b = result.aggregates) === null || _b === void 0 ? void 0 : _b.contextId,
                    instanceId: (_c = result.aggregates) === null || _c === void 0 ? void 0 : _c.instanceId,
                },
                __logging_error__: errMsg,
            };
        }
        return [msg, result];
    }
    printLog(level, message, data) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const [msg, logData] = this.handleLogData(message, data);
        switch (level) {
            // case "trace":
            case "warn":
            case "error":
                if (typeof navigator !== "undefined" && navigator.product === "ReactNative") {
                    console[level](`${(_b = (_a = logData["aggregates"]) === null || _a === void 0 ? void 0 : _a.chain) !== null && _b !== void 0 ? _b : ""} ${msg}`, logData);
                }
                else if (typeof window !== "undefined" && typeof ((_c = window.console) === null || _c === void 0 ? void 0 : _c.error) === "function") {
                    window.console[level](`%c ${(_e = (_d = logData["aggregates"]) === null || _d === void 0 ? void 0 : _d.chain) !== null && _e !== void 0 ? _e : ""} ${msg}`, colors_1.browserStyles[level], [logData]);
                }
                else {
                    this.debugLogger(`%c ${(_g = (_f = logData["aggregates"]) === null || _f === void 0 ? void 0 : _f.chain) !== null && _g !== void 0 ? _g : ""} ${msg}`, colors_1.browserStyles[level], [logData]);
                }
                break;
            default:
                if (typeof navigator !== "undefined" && navigator.product === "ReactNative") {
                    console.log(`${(_j = (_h = logData["aggregates"]) === null || _h === void 0 ? void 0 : _h.chain) !== null && _j !== void 0 ? _j : ""} ${msg}`, logData);
                }
                else {
                    this.debugLogger(`%c ${(_l = (_k = logData["aggregates"]) === null || _k === void 0 ? void 0 : _k.chain) !== null && _l !== void 0 ? _l : ""} ${msg}`, colors_1.browserStyles[level], [logData]);
                }
                break;
        }
    }
    proxyToEmitter(emitter, events) {
        for (const event of events) {
            this.emitter.on(event, (message, data) => {
                emitter.emit(event, message, data);
            });
        }
        this.proxyingToEmitter = true;
    }
    extend(logger) {
        var _a, _b, _c;
        if (((_a = logger === null || logger === void 0 ? void 0 : logger.messageContext) === null || _a === void 0 ? void 0 : _a.aggregates) != null) {
            for (const [key, val] of Object.entries(logger.messageContext.aggregates)) {
                this.setMessageAggregate(key, val);
            }
        }
        if (((_b = logger === null || logger === void 0 ? void 0 : logger.packageContext) === null || _b === void 0 ? void 0 : _b.aggregates) != null) {
            for (const [key, val] of Object.entries(logger.packageContext.aggregates)) {
                this.setLoggerAggregate(key, val);
            }
        }
        if (((_c = logger === null || logger === void 0 ? void 0 : logger.packageContext) === null || _c === void 0 ? void 0 : _c.meta) != null) {
            for (const [key, val] of Object.entries(logger.packageContext.meta)) {
                this.setLoggerMeta(key, val);
            }
        }
        return this;
    }
    destroy() {
        this.flush();
        this.httpWriter.removePackage(this);
    }
}
exports.LoggerCore = LoggerCore;
//# sourceMappingURL=logger-core.js.map